import { motion } from "framer-motion";
import {
  Radar,
  ShieldCheck,
  Crosshair,
  Cloud,
  Brain,
  Shield,
  Activity,
  Users,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    code: "S-01",
    icon: Radar,
    accent: "blue",
    title: "SOC Modernization",
    lead:
      "End-to-end transformation of legacy SOCs into cloud-native, threat-led operations — maturity benchmark, target-state design, and engineering roadmap.",
    chips: ["SOC Maturity", "Target Op-Model", "Runbooks"],
  },
  {
    code: "S-02",
    icon: ShieldCheck,
    accent: "cyan",
    title: "Microsoft Security Enablement",
    lead:
      "Operationalising the Microsoft Security ecosystem across Defender XDR, Sentinel, Entra, Intune and Purview with reference architectures your auditors will recognise.",
    chips: ["Defender XDR", "Sentinel", "Entra ID"],
  },
  {
    code: "S-03",
    icon: Crosshair,
    accent: "indigo",
    title: "Threat Hunting & Detection Engineering",
    lead:
      "Adversary-aligned detections, KQL content packs and proactive hunting programs — mapped to MITRE ATT&CK and your real telemetry sources.",
    chips: ["KQL Content", "ATT&CK", "Hunt Ops"],
  },
  {
    code: "S-04",
    icon: Cloud,
    accent: "blue",
    title: "Cloud Security Transformation",
    lead:
      "Reference architectures and hardening engagements across Azure, AWS and GCP — landing zones, identity boundaries, data protection and workload security.",
    chips: ["Azure", "AWS", "GCP", "Wiz"],
  },
  {
    code: "S-05",
    icon: Brain,
    accent: "cyan",
    title: "AI Security Readiness",
    lead:
      "Threat-model, govern and harden GenAI / Copilot workloads — prompt-injection defence, model exfiltration controls, AI risk register and policy baselines.",
    chips: ["GenAI Defence", "AI Governance", "LLM RT"],
  },
  {
    code: "S-06",
    icon: Shield,
    accent: "indigo",
    title: "Defender XDR Operations",
    lead:
      "Tuning, response automation and analyst enablement across Defender for Endpoint, Identity, Cloud Apps and Office 365 — from baseline to mature detection.",
    chips: ["MDE", "MDI", "MDCA", "MDO"],
  },
  {
    code: "S-07",
    icon: Activity,
    accent: "blue",
    title: "Sentinel SIEM / SOAR Enablement",
    lead:
      "Greenfield and migration delivery of Microsoft Sentinel — data connectors, analytics rules, workbooks, and SOAR playbooks that cut MTTR meaningfully.",
    chips: ["Sentinel", "SOAR", "Logic Apps"],
  },
  {
    code: "S-08",
    icon: Users,
    accent: "cyan",
    title: "Corporate Security Workshops",
    lead:
      "Cohort-led, hands-on workshops for security architects, SOC analysts and IAM engineers — delivered globally onsite, virtual or hybrid.",
    chips: ["Cohort-Led", "Hands-On", "Global"],
  },
];

const accentMap = {
  blue: {
    dot: "#60A5FA",
    border: "rgba(96,165,250,0.30)",
    soft: "rgba(96,165,250,0.10)",
    glow: "rgba(37,99,235,0.18)",
  },
  cyan: {
    dot: "#22D3EE",
    border: "rgba(34,211,238,0.30)",
    soft: "rgba(34,211,238,0.10)",
    glow: "rgba(6,182,212,0.18)",
  },
  indigo: {
    dot: "#818CF8",
    border: "rgba(129,140,248,0.30)",
    soft: "rgba(129,140,248,0.10)",
    glow: "rgba(99,102,241,0.18)",
  },
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Backdrop */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(800px 420px at 80% 0%, rgba(37,99,235,0.10), transparent 60%), radial-gradient(700px 380px at 10% 100%, rgba(6,182,212,0.08), transparent 65%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(96,165,250,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.6) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-blue-500" />
          <span className="label-eyebrow">02 — Services</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-14 md:mb-16 items-end">
          <h2 className="md:col-span-8 font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] tracking-tighter leading-[1.04] text-white">
            Consulting engagements <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg,#67E8F9 0%,#60A5FA 50%,#A5B4FC 100%)",
              }}
            >
              built for enterprise outcomes.
            </span>
          </h2>
          <p className="md:col-span-4 text-slate-400 leading-relaxed text-base md:text-lg">
            Eight engagement archetypes — scoped, fixed-fee or T&amp;M, delivered
            globally onsite or remote with measurable security outcomes.
          </p>
        </div>

        {/* Service grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => {
            const a = accentMap[s.accent];
            const Icon = s.icon;
            return (
              <motion.article
                key={s.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.18 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`service-card-${s.code.toLowerCase()}`}
                className="group relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 flex flex-col"
                style={{
                  background:
                    "linear-gradient(180deg,rgba(10,12,19,0.85) 0%,rgba(7,10,18,0.95) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset",
                }}
              >
                <div
                  aria-hidden
                  className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: `radial-gradient(340px 200px at 80% 0%, ${a.glow}, transparent 70%)`,
                  }}
                />

                <div className="relative flex items-start justify-between">
                  <div
                    className="h-11 w-11 rounded-lg grid place-items-center transition-transform group-hover:scale-105"
                    style={{
                      background: a.soft,
                      border: `1px solid ${a.border}`,
                      color: a.dot,
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.22em]"
                    style={{ color: a.dot, opacity: 0.55 }}
                  >
                    {s.code}
                  </span>
                </div>

                <h3 className="relative mt-6 font-display font-semibold text-lg md:text-[1.2rem] tracking-tight text-white leading-snug min-h-[3.25rem]">
                  {s.title}
                </h3>
                <p className="relative mt-3 text-[13px] text-slate-400 leading-relaxed">
                  {s.lead}
                </p>

                <div className="relative mt-5 flex flex-wrap gap-1.5">
                  {s.chips.map((c) => (
                    <span
                      key={c}
                      className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border text-slate-300"
                      style={{ borderColor: a.border, background: a.soft }}
                    >
                      {c}
                    </span>
                  ))}
                </div>

                <a
                  href="#contact"
                  data-testid={`service-cta-${s.code.toLowerCase()}`}
                  className="relative mt-6 pt-4 inline-flex items-center justify-between text-xs font-medium transition-colors"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <span style={{ color: a.dot }}>Scope engagement</span>
                  <ArrowUpRight
                    size={13}
                    style={{ color: a.dot }}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </motion.article>
            );
          })}
        </div>

        {/* Bottom strip */}
        <div
          className="mt-14 md:mt-16 rounded-2xl p-7 md:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(37,99,235,0.10) 0%, rgba(6,182,212,0.06) 60%, transparent 100%)",
            border: "1px solid rgba(96,165,250,0.25)",
          }}
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-300">
              Engagement model
            </div>
            <div className="mt-2 font-display font-semibold text-white text-xl md:text-2xl tracking-tight">
              Fixed-fee discovery · Phased delivery · Outcome reporting
            </div>
            <p className="mt-2 text-sm text-slate-400 max-w-2xl">
              Every engagement starts with a 30-min discovery call. Within 48
              hours, you receive a written scope, deliverables and pricing —
              no slides, no boilerplate.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="services-engage-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all"
            style={{
              background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
              color: "#fff",
              boxShadow:
                "0 0 0 1px rgba(96,165,250,0.4), 0 10px 30px -10px rgba(37,99,235,0.5)",
            }}
          >
            Start a scoping call <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
